import Stepper from 'react-stepper-horizontal';
import { Container, Form, ProgressBar } from 'react-bootstrap';
import { delay } from '../util/delay';
import { PageHeader } from '../components/Layout/PageHeader';
import { DataCollectionNotice } from '../components/Layout/DataCollectionNotice';
import { ApplicantInfo } from '../components/ApplicantInfo';
import { PyrotechnicsCompany } from '../components/Pyrotechnics/PyrotechnicsCompany';
import { PyrotechnicsSite } from '../components/Pyrotechnics/PyrotechnicsSite';
import { PlotPlan } from '../components/PlotPlan';
import { LiabilityInsurance } from '../components/LiabilityInsurance';
import { UTPyroLicense } from '../components/Pyrotechnics/UTPyroLicense';
import { SubmitSuccess } from '../components/SubmitSuccess';
import { InternalServerError } from '../components/Errors/InternalServerError';
import { SomethingBroke } from '../components/Errors/SomethingBroke';
import FormClientError from '../components/Errors/FormClientError';
import { useState, useEffect, useCallback } from 'react';
import dayjs from 'dayjs';

export function Pyrotechnics() {
	const [step, setStep] = useState(1);

	const [somethingBroke, setSomethingBroke] = useState(false);

	const [httpStatusCode, setHttpStatusCode] = useState(null);

	const [currApplicant, setCurrApplicant] = useState({
		name: '',
		phone: '',
		address: '',
		city: '',
		state: 'UT',
		zip: '',
		email: '',
	});

	const [currPyroSite, setCurrPyroSite] = useState({
		size: '',
		attendants: '',
		address: '',
		city: '',
		state: 'UT',
		zip: '',
		dateAndTimes: [{ date: '', time: '' }],
	});

	const [currPyroCompany, setCurrPyroCompany] = useState({
		name: '',
		operator: '',
		address: '',
		city: '',
		state: 'UT',
		zip: '',
		license: '',
		phone: '',
	});

	const [currPlotPlan, setCurrPlotPlan] = useState({
		file: null,
		answer: null,
	});

	const [currInsurance, setCurrInsurance] = useState({
		file: null,
		answer: null,
	});

	const [currLicense, setCurrLicense] = useState({ file: null, answer: null });

	const [submitDone, setSubmitDone] = useState(false);

	const createBody = license => {
		const data = {
			name: currApplicant.name,
			phone: currApplicant.phone,
			city: currApplicant.city,
			email: currApplicant.email,
			state: currApplicant.state,
			mailingAddress: currApplicant.address,
			zip: currApplicant.zip,
			pyrotechnicCompany: currPyroCompany.name,
			companyMailAddress: currPyroCompany.address,
			companyMailCity: currPyroCompany.city,
			companyMailZip: currPyroCompany.zip,
			companyMailState: currPyroCompany.state,
			companyPhone: currPyroCompany.phone,
			displayDateAndTimes: JSON.stringify(currPyroSite.dateAndTimes),
			numberOfParticipants: currPyroSite.attendants,
			stateLicenseNumber: currPyroCompany.license,
			operator: currPyroCompany.operator,
			plotPlan: currPlotPlan.answer,
			operatorLicense: license.answer,
			liabilityInsurance: currInsurance.answer,
			acres: currPyroSite.size,
			date: dayjs().format('MM/DD/YYYY'),
			pyroSiteAddress: currPyroSite.address,
			pyroSiteCity: currPyroSite.city,
			pyroSiteState: currPyroSite.state,
			pyroSiteZip: currPyroSite.zip,
		};

		const body = new FormData();
		body.append('file', license.file);
		body.append('file', currPlotPlan.file);
		body.append('file', currInsurance.file);

		for (var key in data) {
			body.append(key, data[key]);
		}

		return body;
	};

	async function submitForm(license) {
		try {
			setSubmitDone(true);

			await delay(500);
			const { status } = await fetch(
				`${process.env.REACT_APP_API_PREFIX_PROTO}${process.env.REACT_APP_API_DOMAIN}${process.env.REACT_APP_API_PREFIX}/pyrotechnic-permit`,
				{
					method: 'POST',
					body: createBody(license),
				}
			);

			setHttpStatusCode(status);
			setSubmitDone(false);
		} catch (err) {
			console.error(err);
			setSomethingBroke(true);
		}
	}

	const incrementStep = useCallback(() => {
		setStep(curStep => curStep + 1);
	}, []);

	const decrementStep = useCallback(() => {
		setStep(curStep => curStep - 1);
	}, []);

	const handleOnNext = (setData = () => {}) => {
		return data => {
			setData(data);
			incrementStep();
		};
	};

	const handleSubmit = (setData = () => {}) => {
		return async data => {
			setData(data);
			setSubmitDone(true);
			submitForm(data);
		};
	};

	useEffect(() => {
		if (httpStatusCode === 200) {
			incrementStep();
		}
	}, [httpStatusCode, incrementStep]);

	if (somethingBroke === true) {
		return <SomethingBroke></SomethingBroke>;
	}

	if (httpStatusCode >= 500) {
		return <InternalServerError></InternalServerError>;
	}

	if (httpStatusCode >= 400) {
		return (
			<FormClientError
				returnToForm={() => {
					setStep(1);
					setHttpStatusCode(null);
				}}
			/>
		);
	}

	const steps = [
		{
			component: (
				<ApplicantInfo
					key="appInfo"
					currApplicant={currApplicant}
					onNext={handleOnNext(setCurrApplicant)}
					finalStep={false}
					disablePrev={true}
					disableNext={false}
				/>
			),
			title: 'Applicant Information',
		},
		{
			component: (
				<PyrotechnicsSite
					key="pyroSite"
					currPyroSite={currPyroSite}
					onNext={handleOnNext(setCurrPyroSite)}
					onPrevious={decrementStep}
					finalStep={false}
					disablePrev={false}
					disableNext={false}
				/>
			),
			title: 'Site Information',
		},
		{
			component: (
				<PyrotechnicsCompany
					key="pyroCompany"
					currPyroCompany={currPyroCompany}
					onNext={handleOnNext(setCurrPyroCompany)}
					onPrevious={decrementStep}
					finalStep={false}
					disablePrev={false}
					disableNext={false}
				/>
			),
			title: 'Company Information',
		},
		{
			component: (
				<PlotPlan
					key="plotPlan"
					currPlotPlan={currPlotPlan}
					onNext={handleOnNext(setCurrPlotPlan)}
					onPrevious={decrementStep}
					finalStep={false}
					disablePrev={false}
					disableNext={false}
				/>
			),
			title: 'Plot Plan',
		},
		{
			component: (
				<LiabilityInsurance
					key="insuranceDetails"
					currInsurance={currInsurance}
					coverageAmount={'3,000,000'}
					onNext={handleOnNext(setCurrInsurance)}
					onPrevious={decrementStep}
					finalStep={false}
					disablePrev={false}
					disableNext={false}
				/>
			),
			title: 'Insurance Details',
		},
		{
			component: (
				<UTPyroLicense
					key="utPyroLic"
					currLicense={currLicense}
					onSubmit={handleSubmit(setCurrLicense)}
					onPrevious={decrementStep}
					finalStep={true}
					disablePrev={false}
					disableNext={false}
					enableLoading={submitDone}
				/>
			),
			title: 'License Details',
		},
		{
			component: (
				<SubmitSuccess
					finalStep={true}
					disablePrev={true}
					disableNext={true}
					key="submitSuccess"
				/>
			),
			title: 'Submission',
		},
	];

	return (
		<Container fluid className="p-0 mb-5 permit">
			<PageHeader title="Pyrotechnics" />

			<Form className="app-container">
				<DataCollectionNotice />

				<div className="stepper">
					<Stepper
						steps={steps.map(({ title }, i) => ({
							title,
							onClick: () => setStep(i + 1),
						}))}
						activeStep={step - 1}
					/>
				</div>

				<div className="bar">
					<ProgressBar className="mb-4" now={(step / steps.length) * 100} />
				</div>

				{steps.map(({ component }, index) =>
					step === index + 1 ? component : ''
				)}
			</Form>
		</Container>
	);
}
